import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router";
import bkstApi from "../../api";
import Images from "../../request/components/Images";
import QuillRTE from "../../common/component/QuillRTE";
import SessionOptions from "../component/SessionOptions";
import { UserContext } from "../../context/UserContext";

export default function EventEntry() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState([]);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [sessions, setSessions] = useState([]);
  const [status, setStatus] = useState("DRAFT");
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (eventId) {
      setLoading(true);
      bkstApi(`/my/class/${eventId}`).then((res) => {
        setImages(res.data.images || []);
        setTitle(res.data.title || "");
        setSubtitle(res.data.subtitle || "");
        setDescription(res.data.description || "");
        setSessions(res.data.options?.session?.options || []);
        setStatus(res.data.status || "DRAFT");
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [eventId]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const arr = [];
    if (!title.trim()) {
      arr.push("Title is required");
    }
    if (status === "ACTIVE" && sessions.length === 0) {
      arr.push("Add at least one session before activating");
    }
    for (const session of sessions) {
      if (!session.date || !session.time || !session.pricing?.amount) {
        arr.push("Each session needs a date, time, and price");
        break;
      }
    }

    setErrors(arr);
    if (arr.length > 0) {
      return;
    }

    const payload = {
      images,
      title,
      subtitle,
      description,
      status,
      options: {
        session: {
          options: sessions,
        },
      },
    };

    const request = eventId
      ? bkstApi.put(`/my/class/${eventId}`, payload)
      : bkstApi.post(`/my/class`, payload);

    request.then(() => {
      navigate("/events");
    });
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Container maxWidth="md">
      <Box mt={6} mb={3}>
        <Typography variant="h3" gutterBottom>
          Event Details
        </Typography>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          Add title, description, photos, and session date/time pricing for your class booking page.
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Paper elevation={0}>
          <Box px={2} pt={4} pb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Images
                      edit={true}
                      showArrows={true}
                      pics={images}
                      onChange={(arr) => setImages(arr)}
                      folder={`img/site/${user.acctId}/tmp`}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <Box>
                      <TextField
                        id="title"
                        value={title}
                        label="Title"
                        name="title"
                        variant="outlined"
                        fullWidth
                        required
                        onChange={(e) => setTitle(e.target.value)}
                        inputProps={{ maxLength: 80 }}
                      />
                    </Box>
                    <Box mt={3}>
                      <TextField
                        id="subtitle"
                        value={subtitle}
                        label="Subtitle"
                        name="subtitle"
                        variant="outlined"
                        fullWidth
                        onChange={(e) => setSubtitle(e.target.value)}
                        inputProps={{ maxLength: 120 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <QuillRTE value={description} onChange={(html) => setDescription(html)} />
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Box my={4}>
          <SessionOptions value={sessions} onChange={setSessions} />
        </Box>

        <Box my={4}>
          <Typography variant="h6" gutterBottom>
            Settings
          </Typography>
          <Paper elevation={0}>
            <Box px={2} pt={3} pb={2}>
              <Typography variant="h6">Availability</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={status === "ACTIVE"}
                    onChange={(e) => setStatus(e.target.checked ? "ACTIVE" : "DRAFT")}
                  />
                }
                label="Event is listed on website"
              />
            </Box>
          </Paper>
        </Box>

        {errors.length > 0 && (
          <Box my={2}>
            <Alert severity="error">{errors.join(". ")}</Alert>
          </Box>
        )}

        <Box my={2} sx={{ display: "flex", gap: "15px" }}>
          <Button
            type="button"
            fullWidth
            variant="outlined"
            color="secondary"
            onClick={() => navigate("/events")}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" fullWidth>
            Save Event
          </Button>
        </Box>
      </form>
    </Container>
  );
}
